import React, { useState, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const MediaGrid = ({ 
  media = [], 
  selectedMedia = [], 
  onMediaSelect, 
  onMediaClick,
  viewMode = 'grid',
  showMediaInfo = true
}) => {
  const [visibleMedia, setVisibleMedia] = React.useState(media);
  const loadMoreRef = React.useRef(null);
  
  const isSelected = (mediaId) => selectedMedia?.includes(mediaId);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  if (viewMode === 'list') {
    return (
      <div className="space-y-2">
        {visibleMedia?.map((item) => (
          <div
            key={item?.id}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-250 cursor-pointer ${
              isSelected(item?.id)
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onClick={() => onMediaClick(item)}
          >
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden">
                <Image
                  src={item?.thumbnail}
                  alt={item?.thumbnailAlt || `Media: ${item?.title}`}
                  className="w-full h-full object-cover"
                />
              </div>
              {item?.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                  <Icon name="Play" size={24} color="white" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {showMediaInfo && (
                <>
                  <h3 className="text-sm md:text-base font-medium mb-1 truncate">
                    {item?.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <span className="data-text">{formatFileSize(item?.size)}</span>
                    {item?.type === 'video' && item?.duration > 0 && (
                      <>
                        <span>•</span>
                        <span className="data-text">{formatDuration(item?.duration)}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{new Date(item.uploadDate)?.toLocaleDateString()}</span>
                  </div>
                </>
              )}
              {!showMediaInfo && (
                <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground">
                  <span className="data-text">{formatFileSize(item?.size)}</span>
                  {item?.type === 'video' && item?.duration > 0 && (
                    <>
                      <span>•</span>
                      <span className="data-text">{formatDuration(item?.duration)}</span>
                    </>
                  )}
                  <span>•</span>
                  <span>{new Date(item.uploadDate)?.toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <button
              onClick={(e) => {
                e?.stopPropagation();
                onMediaSelect(item?.id);
              }}
              className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition-all duration-250 ${
                isSelected(item?.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              <Icon 
                name={isSelected(item?.id) ? 'Check' : 'Plus'} 
                size={20} 
              />
            </button>
          </div>
        ))}
        
        {visibleMedia?.length < media?.length && (
          <div ref={loadMoreRef} className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        {visibleMedia?.map((item) => (
          <div
            key={item?.id}
            className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-250 cursor-pointer ${
              isSelected(item?.id)
                ? 'border-primary shadow-warm-md'
                : 'border-border hover:border-primary/50 hover:shadow-warm-sm'
            }`}
            onClick={() => onMediaClick(item)}
          >
            <div className="relative aspect-[4/3] bg-muted">
              <Image
                src={item?.thumbnail}
                alt={item?.thumbnailAlt || `Media: ${item?.title}`}
                className="w-full h-full object-cover"
              />
              
              {item?.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/90 flex items-center justify-center">
                    <Icon name="Play" size={24} color="var(--color-primary)" />
                  </div>
                </div>
              )}

              {item?.type === 'video' && item?.duration > 0 && (
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/80 text-white text-xs data-text">
                  {formatDuration(item?.duration)}
                </div>
              )}

              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onMediaSelect(item?.id);
                }}
                className={`absolute top-2 right-2 w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center transition-all duration-250 ${
                  isSelected(item?.id)
                    ? 'bg-primary text-primary-foreground scale-100'
                    : 'bg-black/60 text-white scale-0 group-hover:scale-100'
                }`}
              >
                <Icon 
                  name={isSelected(item?.id) ? 'Check' : 'Plus'} 
                  size={18} 
                />
              </button>
            </div>

            <div className="p-3 md:p-4">
              {showMediaInfo && (
                <h3 className="text-sm md:text-base font-medium mb-2 line-clamp-1">
                  {item?.title}
                </h3>
              )}
              <div className="flex items-center justify-between text-xs md:text-sm text-muted-foreground">
                <span className="data-text">{formatFileSize(item?.size)}</span>
                <span>{new Date(item.uploadDate)?.toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {visibleMedia?.length < media?.length && (
        <div ref={loadMoreRef} className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
        </div>
      )}
    </>
  );
};

export default MediaGrid;